import React, { useContext, useRef, useState } from 'react';
import {
  Grid,
  IconButton,
  Typography,
  TextField,
  Button,
  Autocomplete,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb'; // Import the locale you want to use
import { filter, map } from 'lodash';
import { DatePicker } from '@mui/x-date-pickers';
import { ProjectContext } from '../../../Contexts/Project/Context';
import { useQuery } from '@apollo/client/react';
import CurrentUserContext from '@/Contexts/CurrentUser';
import { GetManagersDocument, ActivityInput, EmploymentStatusEnum } from '@/graphql/generated/graphql';
import { isActivityComplete } from './validation';

type ActivityField = 'title' | 'startTime' | 'endTime';

const MultipleActivitiesForm: React.FC = () => {
  const { project, onActivitiesChange } = useContext(ProjectContext);
  const activities = project.activities || [];
  const { currentUser, dateTimePreference } = useContext(CurrentUserContext);

  const { data, loading: managersLoading } = useQuery(GetManagersDocument, {
    skip: !currentUser?.defaultCompany?.id,
  });

  // Stable identities so removing an activity doesn't bleed field state
  // into its neighbours
  const keyCounter = useRef(0);
  const keysRef = useRef<number[]>([]);
  while (keysRef.current.length < activities.length) keysRef.current.push(keyCounter.current++);
  if (keysRef.current.length > activities.length) keysRef.current.length = activities.length;

  // Show "required" errors only after the user has interacted with a field
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (key: number, field: ActivityField) =>
    setTouched((prev) => ({ ...prev, [`${key}-${field}`]: true }));
  const isTouched = (key: number, field: ActivityField) => Boolean(touched[`${key}-${field}`]);

  const handleAddActivity = () => {
    keysRef.current.push(keyCounter.current++);
    onActivitiesChange([...activities, {
      title: "",
      description: "",
      startTime: "",
      endTime: "",
      completedAt: "",
      supervisorId: null,
      colorCode: "#1976d2", // default MUI primary color
    }]);
  };

  const handleRemoveActivity = (index: number) => {
    keysRef.current = [...keysRef.current.slice(0, index), ...keysRef.current.slice(index + 1)];
    const newActivities = filter(activities, (_, i) => i !== index);
    onActivitiesChange(newActivities);
  };

  const handleActivityChange = (index: number, key: keyof ActivityInput, value: string | null) => {
    const newActivities = map(activities, (activity, i) => {
      if (i === index) {
        return { ...activity, [key]: value };
      }
      return activity;
    });

    onActivitiesChange(newActivities);
  };

  const handleStartTimeChange = (index: number, value: dayjs.Dayjs | null) => {
    handleActivityChange(
      index,
      'startTime',
      value ? value.startOf('day').toISOString() : null
    );
  };

  const disableAdd = activities.some(activity => !isActivityComplete(activity));

  const supervisorOptions = filter(data?.getManagers || [], (manager) => manager.user?.companyEmployment?.status === EmploymentStatusEnum.Active);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Grid container spacing={3}>
        {activities.map((activity, index) => {
          const itemKey = keysRef.current[index];
          const titleError = (isTouched(itemKey, 'title') || activity.title !== "") && !activity.title;
          const startError = isTouched(itemKey, 'startTime') && !activity.startTime;
          const endError = isTouched(itemKey, 'endTime') && !activity.endTime;

          return (
            <Grid key={itemKey} size={12}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Grid container alignItems="center">
                    <Grid size="grow">
                      <Typography variant="h6">
                        Activity {index + 1}
                      </Typography>
                    </Grid>
                    <Grid>
                      <IconButton
                        onClick={() => handleRemoveActivity(index)}
                        color="error"
                        size="small"
                        aria-label={`Remove activity ${index + 1}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    required
                    label="Title"
                    value={activity.title}
                    onChange={(e) => handleActivityChange(index, 'title', e.target.value)}
                    onBlur={() => markTouched(itemKey, 'title')}
                    error={titleError}
                    helperText={titleError ? "Title is required" : ""}
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    value={activity.description ?? ""}
                    onChange={(e) => handleActivityChange(index, 'description', e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label="Start Date"
                    value={activity.startTime ? dayjs(activity.startTime) : null}
                    onChange={(value) => handleStartTimeChange(index, value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: startError,
                        helperText: startError ? 'Start date is required' : '',
                        onBlur: () => markTouched(itemKey, 'startTime'),
                      }
                    }}
                    format={dateTimePreference.date}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label="End Date"
                    value={activity.endTime ? dayjs(activity.endTime) : null}
                    onChange={(value) => handleActivityChange(
                      index,
                      'endTime',
                      value ? value.endOf('day').toISOString() : null
                    )}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: endError,
                        helperText: endError ? 'End date is required' : '',
                        onBlur: () => markTouched(itemKey, 'endTime'),
                      }
                    }}
                    format={dateTimePreference.date}
                    minDate={activity.startTime ? dayjs(activity.startTime) : undefined}
                  />
                </Grid>

                <Grid size={12}>
                  <Autocomplete
                    id={`supervisor-selector-${index}`}
                    options={supervisorOptions}
                    loading={managersLoading}
                    autoHighlight
                    fullWidth
                    value={supervisorOptions.find(manager => manager.user?.companyEmployment?.id === activity.supervisorId) || null}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(_, newValue) => handleActivityChange(index, 'supervisorId', newValue?.user?.companyEmployment?.id || null)}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supervisor"
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            autoComplete: "off",
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Color Code"
                    type="color"
                    value={activity.colorCode}
                    onChange={(e) => handleActivityChange(index, 'colorCode', e.target.value)}
                    sx={{ '& input': { height: 40, cursor: 'pointer' } }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}

        <Grid container spacing={2} mt={2} size={12}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddActivity}
              variant="outlined"
              fullWidth
              disabled={disableAdd}
            >
              {activities.length > 0 ? "Add Another Activity" : "Add Activity"}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default MultipleActivitiesForm;
